import React, { useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import { useDispatch, useSelector } from 'react-redux';
import { getStlifeList } from '../../store/thunks/studentLifeThunk';
import { useNavigate } from 'react-router-dom';
import { addStlife, updateStLifeStatus } from '../../services/studentLife';
import { setData } from '../../store/slices/studentLifeSlice';
import { logout } from '../../store/thunks/authThunks';
import swal from 'sweetalert';

const StudentLife = () => {
    const [showModal, setShowModal] = useState(false);
    const [showWarn, setShowWarn] = useState(false);
    const authToken = useSelector((state) => state.auth.token);
    const [categoryName, setCategoryName] = useState('');
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { isAuthenticated } = useSelector((state) => state.auth);
    const { list, total, loading, error } = useSelector((state) => state.studentLife);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(null);
    const token = useSelector((state) => state.auth.token);
    
    useEffect(() => {
      if (!isAuthenticated && error?.message === 'unauthorized') {
        navigate('/login');
      }
    }, [isAuthenticated, error, navigate]);

    useEffect(() => {
      if(error && error.toLowerCase()==='invalid token'){
        handleLogout();
      }    
    }, [error]);
  
    useEffect(() => {
      if (token) {
        dispatch(getStlifeList({ page, limit: rowsPerPage, sortField, sortOrder }));
      }    
    }, [dispatch, page, rowsPerPage, sortField, sortOrder, token]);
  
    const columns = [
      { name: 'Title', selector: (row) => row.title, sortable: true },
      {
        name: 'Images Count',
        selector: (row) => row.images?.length || 0,
        sortable: false,
        cell: (row) => (
          <div>{row.images?.length || 0}</div>
        )
      },
      { name: 'Active', selector: (row) => row.active, sortable: false, cell: (row) => (
        <label className="inline-flex items-center cursor-pointer relative">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={row.active}
            onChange={() => handleToggle(row)}
          />
          <div className="w-11 h-6 bg-gray-200 rounded-full peer-checked:bg-green-500 transition-colors"></div>
          <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-300 peer-checked:translate-x-5"></div>
        </label>
      ) },
      {
        name: 'Add Images',
        sortable: false,
        cell: (row) => (
          <button
            onClick={() => handleAddImage(row._id, row.title)}
            className="text-blue-600 hover:underline cursor-pointer"
          >
            Add Images
          </button>
        )
      },
      {
        name: 'Images',
        sortable: false,
        cell: (row) => (
          <button
            onClick={() => navigate(`/student-life/images/${row._id}/${row.title}`)}
            className="text-blue-600 hover:underline cursor-pointer"
          >
            Images
          </button>
        )
      }
    ];
  
    const handleToggle = async (row) => {
      await updateStLifeStatus(row._id, !row.active, token.token)
          .then(response=>{
            if(response?.data?.success){
              const updatedList = list.map(item => 
                item._id === row._id ? { ...item, active: !item.active } : item
              );
          
              dispatch(setData({data: updatedList, total: total}))
            }
          })
          .catch(error=>{
            console.log(error);
          });
    };

    const handleLogout = () => {
        dispatch(logout());
        navigate('/login');
      };
  
    const handleAddImage = (id, title) => {
      navigate('/student-life/add-images', { state: { id, title } }); // or open modal instead
    };
  
    const handleAddCategory = () => {
      setShowModal(true);
      setShowWarn(false);
    };
  
    const closeModal = () => {
      setShowModal(false);
      setShowWarn(false);
    };
  
    const saveCategory = async () => {
      try {
        if(categoryName){
          const resp = await addStlife(categoryName, authToken.token);
          if(resp?.data?.success){
            closeModal();
            swal({
                title: "Category",
                text: resp?.data?.message,
                icon: "success",
                buttons: true,
              });
            dispatch(getStlifeList({ page, limit: rowsPerPage, sortField, sortOrder }));
          }
        }else{
          setShowWarn(true);
        }
      } catch (error) {
        console.log(error);
        swal({
              title: "Category",
              text: error?.response?.data?.error,
              icon: "error",
              buttons: true,
            });
      }    
    }
  
    const handleChange = (e) => {
      setCategoryName(e.target.value);
      setShowWarn(false);
    }
  
    return (
      <div className="p-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold">Student Life</h2>
            <button
              onClick={handleAddCategory}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition cursor-pointer"
            >
              + Add Category
            </button>
          </div>
          <DataTable
            columns={columns}
            data={list}
            progressPending={loading}
            pagination
            paginationServer
            paginationPerPage={rowsPerPage}
            paginationTotalRows={total}
            onChangePage={(page) => setPage(page)}
            onChangeRowsPerPage={(newPerPage, page) => {
              setRowsPerPage(newPerPage);
              setPage(page);
            }}
            onSort={(column, sortDirection) => {
              setSortField(column.sortField);     // or column.name if API expects field name
              setSortOrder(sortDirection);
            }}
            highlightOnHover
            pointerOnHover
            striped
          />
  
          {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-lg">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Add Category</h2>
                <button
                  className="text-gray-600 hover:text-gray-800 cursor-pointer"
                  onClick={closeModal}
                >
                  ✕
                </button>
              </div>
  
              {showWarn && <div className="text-red-500">*Please add category name.</div>}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Category Name*</label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 px-3 py-2 rounded"
                    placeholder="Enter category name"
                    name="category_name"
                    value={categoryName}
                    onChange={handleChange}
                  />
                </div>
                {/* Add more form fields here if needed */}
  
                <div className="text-right">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="bg-gray-100 text-gray-800 px-4 py-2 rounded mr-2 cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 cursor-pointer"
                    onClick={saveCategory}
                  >
                    Save
                  </button>
                </div>
            </div>
          </div>
        )}
      </div>
    )
}

export default StudentLife
